import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface CompteFormateurMatiere {
  matiere_id: number;
  code_matiere: string;
  nom_matiere: string;
  niveau: string;
  semestre: number;
  option: string;
}

export interface CompteFormateur {
  id: number;
  username: string;
  nom_complet: string;
  email: string;
  telephone: string;
  telephone_secondaire: string;
  specialite: string;
  enseignant_id: number | null;
  semestre_actif: string;
  annee_academique_actif: string;
  mot_de_passe_temporaire: string;
  mot_de_passe_change: boolean;
  must_change_password: boolean;
  actif: boolean;
  derniere_connexion: string | null;
  matieres: CompteFormateurMatiere[];
  nb_matieres: number;
  niveaux: string[];
}

export interface CreerComptesPayload {
  enseignant_ids: number[];
  semestre: 'S1' | 'S2';
  annee_academique: string;
  regenerer_si_existant?: boolean;
}

export interface CreerComptesResultat {
  enseignant_id: number;
  success: boolean;
  compte_cree?: boolean;
  compte_renouvele?: boolean;
  user_id?: number;
  username?: string;
  nom_complet?: string;
  mot_de_passe_temporaire?: string;
  message?: string;
}

export interface CreerComptesResponse {
  success: boolean;
  semestre: string;
  annee_academique: string;
  total_traites: number;
  nb_crees: number;
  nb_renouveles: number;
  nb_ignores: number;
  resultats: CreerComptesResultat[];
}

export interface FormateurConnecte {
  id: number;
  username: string;
  nom_complet: string;
  derniere_connexion: string;
  telephone: string;
  semestre_actif: string;
}

export const comptesFormateursApi = createApi({
  reducerPath: 'comptesFormateursApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['ComptesFormateurs', 'FormateursConnectes'],
  endpoints: (b) => ({
    listerComptes: b.query<{
      total: number;
      nb_mdp_non_change: number;
      nb_mdp_change: number;
      resultats: CompteFormateur[];
    }, {
      semestre?: string;
      annee_academique?: string;
      statut?: 'all' | 'mdp_non_change' | 'mdp_change';
      pole?: string;
      search?: string;
    } | void>({
      query: (params) => ({
        url: '/comptes-formateurs/',
        params: (params || {}) as any,
      }),
      providesTags: ['ComptesFormateurs'],
    }),
    creerComptes: b.mutation<CreerComptesResponse, CreerComptesPayload>({
      query: (body) => ({
        url: '/comptes-formateurs/creer/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ComptesFormateurs'],
    }),
    renouvelerCompte: b.mutation<{
      success: boolean; user_id: number;
      nouveau_mot_de_passe: string; nom_complet: string;
    }, { userId: number; semestre?: string; annee_academique?: string }>({
      query: ({ userId, ...body }) => ({
        url: `/comptes-formateurs/${userId}/renouveler/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ComptesFormateurs'],
    }),
    desactiverCompte: b.mutation<{ success: boolean }, number>({
      query: (userId) => ({
        url: `/comptes-formateurs/${userId}/desactiver/`,
        method: 'POST',
      }),
      invalidatesTags: ['ComptesFormateurs'],
    }),
    formateursConnectes: b.query<{
      fenetre_minutes: number;
      total_connectes: number;
      resultats: FormateurConnecte[];
    }, { minutes?: number } | void>({
      query: (params) => ({
        url: '/comptes-formateurs/connectes/',
        params: params as any,
      }),
      providesTags: ['FormateursConnectes'],
    }),
  }),
});

export const {
  useListerComptesQuery,
  useCreerComptesMutation,
  useRenouvelerCompteMutation,
  useDesactiverCompteMutation,
  useFormateursConnectesQuery,
} = comptesFormateursApi;
